import { PivotTableGroup } from './enums'
import type { PivotTableFields, PivotTablePercentFields } from './types'

/** A percent field's numerator is either one column or several to be added together. */
export const toColumns = (numerator: string | string[]) =>
  Array.isArray(numerator) ? numerator : [numerator]

/** Columns a percent field reads; must reach the aggregated CTE even when the user hides them. */
export const getPercentFieldColumns = (percentFields?: PivotTablePercentFields) =>
  (percentFields ?? []).flatMap(([, numerator, denominator]) => [denominator, ...toColumns(numerator)])

/** A percent field has no column of its own in the raw table — summing one there is a DuckDB binder error. */
export const excludePercentFieldNames = (keys: string[], percentFields?: PivotTablePercentFields) =>
  keys.filter(key => !percentFields?.some(([fieldName]) => fieldName === key))

/** VALUES-group keys minus percent fields — the set getExportData can safely SUM against the raw table. */
export const getExportableValueFields = (inputFields: PivotTableFields, percentFields?: PivotTablePercentFields) =>
  excludePercentFieldNames(
    Object.entries(inputFields).filter(([, field]) => field.group === PivotTableGroup.VALUES).map(([key]) => key),
    percentFields,
  )

/**
 * `fieldName = ((SUM(a) + SUM(b)) / SUM(denominator)) * 100`, NULL when the denominator sums to 0
 * (no answer, not zero — e.g. a grand total whose Value Starts cancel). No COALESCE around the
 * numerator either: a stale-cache column that's all NULL must render blank, never a faked zero.
 */
export const getPercentSelectCondition = (
  [fieldName, numerator, denominator]: PivotTablePercentFields[number],
) => {
  const numeratorSum = toColumns(numerator).map(column => `SUM(${column})`).join(' + ')
  return `CASE WHEN SUM(${denominator}) = 0 THEN NULL ELSE ((${numeratorSum}) / SUM(${denominator})) * 100 END AS ${fieldName}`
}
